import type { EstimateHistoryPoint, Report } from "../types";
import { mergeReportHistory } from "./reportHistory";
import { STUDIO_MARKET_STALE_HOURS } from "../studio/validation";
import { currentMarketProbability, externalConnection, isExternalMarket, marketRuntimeSnapshot } from "../studio/marketConnection";
import { isProbability, type ConfidenceLevel, type StudioReport } from "../studio/types";
import type { MarketDataSessionState } from "../services/marketDataSession";

export interface MarketProbabilitySeriesProvider {
  getSeries(reportId: string): EstimateHistoryPoint[];
}

export interface ReportHeroData {
  reportId: string;
  title: string;
  marketProbability: number | null;
  ftmMidpoint: number | null;
  rangeLow: number | null;
  rangeHigh: number | null;
  divergence: number | null;
  confidence: ConfidenceLevel | null;
  observedAt: string | null;
  marketUpdatedAt: string | null;
  freshness: "current" | "aging" | "stale" | "unavailable" | "demo";
  sourceLabel: string;
  isDemo: boolean;
  history: EstimateHistoryPoint[];
}

export function createStudioReportHeroData(report: StudioReport, seriesProvider?: MarketProbabilitySeriesProvider, now = new Date(), runtime?: MarketDataSessionState | null): ReportHeroData {
  const savedHistory = seriesProvider?.getSeries(report.reportId) ?? report.probabilityHistory ?? [];
  const marketSnapshot = marketRuntimeSnapshot(runtime);
  const marketHistory = runtime?.history?.points.map((point) => ({ observedAt: point.observedAt, marketProbability: point.probability, ftmEstimateLow: null, ftmEstimateHigh: null, ftmMid: null, versionId: null, updateReason: null, isEstimateUpdate: false })) ?? [];
  const history = mergeHistory(savedHistory, marketHistory);
  const marketProbability = isExternalMarket(report.market) ? currentMarketProbability(report.market, runtime) : validProbability(report.market.currentMarketProbability);
  const ftmMidpoint = validProbability(report.synthesis.ftmEstimate) ? report.synthesis.ftmEstimate : latestMidpoint(history);
  const marketUpdatedAt = isExternalMarket(report.market) ? marketSnapshot?.observedAt ?? marketSnapshot?.receivedAt ?? null : report.market.marketPriceUpdatedAt;
  const isDemo = report.isDemo === true;
  return buildHeroData({
    reportId: report.reportId,
    title: report.market.title,
    marketProbability,
    ftmMidpoint,
    rangeLow: validProbability(report.synthesis.rangeLow),
    rangeHigh: validProbability(report.synthesis.rangeHigh),
    confidence: report.synthesis.estimateConfidence,
    observedAt: latestObservedAt(history) ?? marketUpdatedAt,
    marketUpdatedAt,
    history,
    isDemo,
    now,
    marketFreshness: isExternalMarket(report.market) ? marketSnapshot?.freshness ?? "unavailable" : undefined,
    sourceLabel: isExternalMarket(report.market) ? (marketSnapshot ? "Market data" : externalConnection(report.market) ? "Latest market data" : "Unavailable") : undefined,
  });
}

export function createPublicReportHeroData(report: Report, now = new Date()): ReportHeroData {
  const history = mergeReportHistory(report);
  const latest = latestPoint(history);
  const marketProbability = validProbability(report.marketProbability);
  const ftmMidpoint = latestMidpoint(history);
  const marketUpdatedAt = latestMarketObservedAt(history) ?? report.publishedAt ?? null;
  return buildHeroData({
    reportId: report.reportId,
    title: report.title,
    marketProbability,
    ftmMidpoint,
    rangeLow: validProbability(report.ftmEstimateLow),
    rangeHigh: validProbability(report.ftmEstimateHigh),
    confidence: report.confidence ?? null,
    observedAt: latest?.observedAt ?? report.publishedAt ?? null,
    marketUpdatedAt,
    history,
    isDemo: false,
    now,
    sourceLabel: "Published snapshot",
  });
}

function buildHeroData(input: {
  reportId: string;
  title: string;
  marketProbability: number | null;
  ftmMidpoint: number | null;
  rangeLow: number | null;
  rangeHigh: number | null;
  confidence: ConfidenceLevel | null;
  observedAt: string | null;
  marketUpdatedAt: string | null;
  history: EstimateHistoryPoint[];
  isDemo: boolean;
  now: Date;
  sourceLabel?: string;
  marketFreshness?: "fresh" | "aging" | "stale" | "unavailable";
}): ReportHeroData {
  const divergence = input.marketProbability !== null && input.ftmMidpoint !== null ? input.ftmMidpoint - input.marketProbability : null;
  const freshness = input.isDemo ? "demo" : input.marketFreshness ? mapFreshness(input.marketFreshness) : freshnessFor(input.marketProbability, input.marketUpdatedAt, input.now);
  const sourceLabel = input.isDemo ? "Local demo" : input.sourceLabel ?? (input.marketProbability === null || !input.marketUpdatedAt ? "Unavailable" : "Latest market data");
  return { reportId: input.reportId, title: input.title, marketProbability: input.marketProbability, ftmMidpoint: input.ftmMidpoint, rangeLow: input.rangeLow, rangeHigh: input.rangeHigh, divergence, confidence: input.confidence, observedAt: input.observedAt, marketUpdatedAt: input.marketUpdatedAt, freshness, sourceLabel, isDemo: input.isDemo, history: input.history };
}

function mapFreshness(value: "fresh" | "aging" | "stale" | "unavailable"): ReportHeroData["freshness"] { return value === "fresh" ? "current" : value; }

function freshnessFor(marketProbability: number | null, updatedAt: string | null, now: Date): ReportHeroData["freshness"] {
  if (marketProbability === null || !updatedAt) return "unavailable";
  const ageHours = (now.getTime() - new Date(updatedAt).getTime()) / 36e5;
  return Number.isFinite(ageHours) && ageHours > STUDIO_MARKET_STALE_HOURS ? "stale" : "current";
}

function latestPoint(history: EstimateHistoryPoint[]): EstimateHistoryPoint | null {
  const sorted = [...history].sort((left, right) => left.observedAt.localeCompare(right.observedAt));
  return sorted[sorted.length - 1] ?? null;
}

function latestMidpoint(history: EstimateHistoryPoint[]): number | null {
  return [...history].sort((left, right) => left.observedAt.localeCompare(right.observedAt)).reverse().find((point) => validProbability(point.ftmMid))?.ftmMid ?? null;
}

function latestObservedAt(history: EstimateHistoryPoint[]): string | null {
  return latestPoint(history)?.observedAt ?? null;
}

function latestMarketObservedAt(history: EstimateHistoryPoint[]): string | null {
  return [...history].sort((left, right) => left.observedAt.localeCompare(right.observedAt)).reverse().find((point) => validProbability(point.marketProbability))?.observedAt ?? null;
}

function validProbability(value: number | null | undefined): number | null {
  return isProbability(value) ? value : null;
}

function mergeHistory(saved: EstimateHistoryPoint[], market: EstimateHistoryPoint[]): EstimateHistoryPoint[] {
  const byObservedAt = new Map<string, EstimateHistoryPoint>();
  [...saved, ...market].forEach((point) => {
    const previous = byObservedAt.get(point.observedAt);
    byObservedAt.set(point.observedAt, previous ? { ...previous, ...point, marketProbability: point.marketProbability ?? previous.marketProbability, ftmEstimateLow: point.ftmEstimateLow ?? previous.ftmEstimateLow, ftmEstimateHigh: point.ftmEstimateHigh ?? previous.ftmEstimateHigh, ftmMid: point.ftmMid ?? previous.ftmMid } : point);
  });
  return [...byObservedAt.values()].sort((left, right) => left.observedAt.localeCompare(right.observedAt));
}
